import React, { useMemo } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { SearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { TFunction } from "i18next/typescript/t";
import { useSelector } from "react-redux";

import FilterRenderer from "@/components_v2/Filters/FilterRenderer";
import { FilterField } from "@/components_v2/Filters/types";
import {
  HookFormBaseDropdown,
  HookFormInfiniteScrollDropdown,
  HookFormInput,
} from "@/components_v2/FormFields/ReactHookForm/fields";
import {
  useLazyGetOrganizationsQuery,
  useLazyGetSurveysQuery,
} from "@/redux/service";
import {
  SurveyViewDataFormat,
  SurveyViewDistributionFormat,
} from "@/shared/utils/constants";
import { SurveyResponse, SurveyQueryParams } from "@/redux/survey/types";
import {
  BaseOrganizationResponse,
  OrganizationQueryParams,
} from "@/redux/organizations/types";
import {
  SurveyViewFiltersState,
  SurveyViewsFiltersProps,
} from "@/domains/survey-views/types";
import { selectCurrentUser } from "@/redux/authentication/selectors";

import { useFilterCount } from "@/shared/hooks/useFilterCount";

const surveyViewsFiltersSchema = (t: TFunction) =>
  yup
    .object()
    .shape({
      title: yup.string().typeError(t("form.validation.notValid")),
      survey: yup.number().typeError(t("form.validation.notValid")),
      dataFormat: yup
        .array()
        .of(yup.string().typeError(t("form.validation.notValid"))),
      distributionFormat: yup
        .array()
        .of(yup.string().typeError(t("form.validation.notValid"))),
      organizations: yup
        .array()
        .of(yup.number().typeError(t("form.validation.notValid"))),
    })
    .required();

const SurveyViewFilters = ({
  filterNames,
  setFilters,
  defaultFilters,
}: SurveyViewsFiltersProps) => {
  const { t } = useTranslation();
  const [getSurveys] = useLazyGetSurveysQuery();
  const [getOrganizations] = useLazyGetOrganizationsQuery();
  const currentUser = useSelector(selectCurrentUser);

  const form = useForm<SurveyViewFiltersState>({
    // @ts-expect-error filters state is same as schema
    resolver: yupResolver(surveyViewsFiltersSchema(t)),
    mode: "onChange",
    defaultValues: {
      title: "",
      survey: undefined,
      dataFormat: "",
      distributionFormat: "",
      ...defaultFilters,
    },
  });

  const filterFields: Record<string, FilterField> = useMemo(() => {
    return {
      search: {
        name: "title",
        isAdvancedFilter: false,
        isDebounced: true,
        component: (
          <HookFormInput
            name="title"
            control={form.control}
            className="data-blocks-filters-search"
            suffix={<SearchOutlined />}
            placeholder={t("surveyViews.placeholders.searchSurveyViews")}
          />
        ),
      },
      survey: {
        name: "survey",
        label: t("surveyViews.labels.survey"),
        isAdvancedFilter: true,
        isDebounced: false,
        component: (
          <HookFormInfiniteScrollDropdown<SurveyResponse, SurveyQueryParams>
            name="survey"
            control={form.control}
            fetchFn={getSurveys}
            labelFieldName="title"
            valueFieldName="id"
            searchFieldName="title"
            placeholder={t("surveyViews.placeholders.survey")}
            allowClear={true}
          />
        ),
      },
      dataFormat: {
        name: "dataFormat",
        label: t("surveyViews.labels.dataFormat"),
        isAdvancedFilter: true,
        isDebounced: false,
        component: (
          <HookFormBaseDropdown
            name="dataFormat"
            control={form.control}
            options={SurveyViewDataFormat.asList()}
            multiple
            placeholder={t("surveyViews.placeholders.version")}
            allowClear={true}
          />
        ),
      },
      distributionFormat: {
        name: "distributionFormat",
        label: t("surveyViews.labels.distributionFormat"),
        isAdvancedFilter: true,
        isDebounced: false,
        component: (
          <HookFormBaseDropdown
            name="distributionFormat"
            control={form.control}
            options={SurveyViewDistributionFormat.asList()}
            multiple
            placeholder={t("surveyViews.placeholders.distributionFormat")}
            allowClear={true}
          />
        ),
      },
      organizations: {
        name: "organizations",
        label: t("surveyViews.labels.organizations"),
        isAdvancedFilter: true,
        isDebounced: false,
        component: (
          <HookFormInfiniteScrollDropdown<
            BaseOrganizationResponse,
            OrganizationQueryParams
          >
            name="organizations"
            control={form.control}
            multiple
            fetchFn={getOrganizations}
            labelFieldName="name"
            valueFieldName="id"
            searchFieldName="name"
            placeholder={t("surveyViews.placeholders.organizations")}
            defaultFilters={
              currentUser?.organization?.id
                ? { exclude: currentUser.organization.id }
                : {}
            }
            allowClear={true}
          />
        ),
      },
    };
  }, [currentUser]);

  const activeFilterCount = useFilterCount<SurveyViewFiltersState>(
    form,
    defaultFilters,
  );

  return (
    <FilterRenderer
      form={form}
      filters={filterFields}
      activeFilterNames={filterNames}
      popoverTitle={t("datablocks.labels.filters")}
      handleClear={() => {
        form.reset(defaultFilters);
        setFilters(form.getValues());
      }}
      onFiltersChange={setFilters}
      areFiltersDirty={form.formState.isDirty}
      activeFilterCount={activeFilterCount}
      handleSearch={() => setFilters(form.getValues())}
      className="survey-view-filters"
    />
  );
};

export default SurveyViewFilters;
